var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import '../icon/Icon.js';
import { getStatusColor } from '../../utils/color';
let ActionCard = class ActionCard extends LitElement {
    constructor() {
        super(...arguments);
        this.handleRun = () => this.emit('run-node');
        this.handleSettings = () => this.emit('settings-node');
        this.handleDelete = () => this.emit('delete-node');
        this.handleShowLogs = () => this.emit('show-logs');
    }
    /* ---------------------------------- */
    /* Derived state                      */
    /* ---------------------------------- */
    get status() {
        return this.nodeExecution?.status ?? null;
    }
    get logsOnly() {
        return Boolean(this.nodeExecution);
    }
    get borderColor() {
        // Utilise les variables CSS déclarées si possible
        const status = this.status?.toLowerCase() ?? '';
        const color = getStatusColor(status);
        return `var(--color-${color})`;
    }
    /* ---------------------------------- */
    /* Assets                             */
    /* ---------------------------------- */
    getIcon(path) {
        return new URL(`../../assets/${path}`, import.meta.url).href;
    }
    get nodeIcon() {
        return this.node?.icon
            ? this.getIcon(`${this.node.icon}.svg`)
            : this.getIcon('default_gray.svg');
    }
    /* ---------------------------------- */
    /* Events                             */
    /* ---------------------------------- */
    updated(changedProperties) {
        if (changedProperties.has('nodeExecution')) {
            this.style.setProperty('--border-color', this.borderColor);
        }
    }
    emit(name) {
        this.dispatchEvent(new CustomEvent(name, {
            detail: { node: this.node, nodeExecution: this.nodeExecution },
            bubbles: true,
            composed: true,
        }));
    }
    /* ---------------------------------- */
    /* Render                             */
    /* ---------------------------------- */
    render() {
        return html `
      <h3 class="name">${this.node.name}</h3>
      <div class="icon-container">
        <img class="icon" src="${this.nodeIcon}" alt=${msg('Node Icon', { id: 'card.nodeIcon', desc: 'Alt text for the workflow node icon image' })} />
      </div>
      <div class="buttons">
        ${this.logsOnly
            ? html `<div class="button" @click=${this.handleShowLogs}>
              <basic-icon name="logs" label=${msg('Logs', { id: 'card.action.logs', desc: 'Label for the show-logs action button on a workflow node card' })}></basic-icon>
            </div>`
            : html `
              <div class="button" @click=${this.handleRun}>
                <basic-icon name="run" label=${msg('Run', { id: 'card.action.run', desc: 'Label for the run-node action button on a workflow node card' })}></basic-icon>
              </div>
              <div class="button" @click=${this.handleSettings}>
                <basic-icon name="settings" label=${msg('Settings', { id: 'card.action.settings', desc: 'Label for the settings action button on a workflow node card' })}></basic-icon>
              </div>
              <div class="button" @click=${this.handleDelete}>
                <basic-icon name="delete" label=${msg('Delete', { id: 'card.action.delete', desc: 'Label for the delete action button on a workflow node card' })}></basic-icon>
              </div>
            `}
      </div>
    `;
    }
};
/* ---------------------------------- */
/* Styles                             */
/* ---------------------------------- */
ActionCard.styles = css `
    :host {
      /* Couleurs alignées sur SimpleButton */
      --color-primary: #0078d4;
      --color-secondary: #f0f0f0;
      --color-success: #28a745;
      --color-danger: #dc3545;
      --color-warning: #ffc107;
      --color-default: #d9d9d9;
      --border-color: var(--color-default, #ccc);
      display: block;
      width: 200px;
      height: 125px;
      border: 2px solid var(--border-color);
      border-radius: 10px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
      padding: 8px;
      box-sizing: border-box;
      background: #fafafaff;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
    }

    h3.name {
      align-self: flex-start;
      margin: 0 0 6px 0;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.1;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .icon-container {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 6px;
    }

    .icon-container img.icon {
      width: 36px;
      height: 36px;
    }

    .buttons {
      display: flex;
      justify-content: center;
      gap: 6px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s;
    }

    :host(:hover) .buttons {
      opacity: 1;
      pointer-events: auto;
    }

    .button {
      cursor: pointer;
      background: #fff;
      border-radius: 50%;
      box-shadow: 0 1px 2px rgba(0,0,0,0.07);
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: box-shadow 0.15s;
    }

    .button:hover {
      box-shadow: 0 2px 6px rgba(0,0,0,0.13);
    }

    .button img {
      width: 18px;
      height: 18px;
      display: block;
    }
  `;
__decorate([
    property({ type: Object })
], ActionCard.prototype, "node", void 0);
__decorate([
    property({ type: Object })
], ActionCard.prototype, "nodeExecution", void 0);
ActionCard = __decorate([
    localized(),
    customElement('action-card')
], ActionCard);
export { ActionCard };
//# sourceMappingURL=ActionCard.js.map